import {
	AppButton,
	Checkbox,
	IntegrationLogo,
	ListItem,
	MenuList,
	SearchableMenu,
	useMenuRoomFit
} from '@clickguard/design-system'
import ChevronDown from '@untitled-ui/icons-react/build/cjs/ChevronDown'
import {
	KeyboardEvent as ReactKeyboardEvent,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import { RulePlatform } from './types'

export interface MultiSelectOption {
	value: string
	label: string
	/** Ad platform the option belongs to, when it has one; the row carries its mark. */
	platform?: RulePlatform
}

interface MultiSelectProps {
	options: MultiSelectOption[]
	values: string[]
	onChange: (values: string[]) => void
	/** Trigger text when nothing is picked. */
	placeholder: string
	ariaLabel: string
	/** Container the panel portals into — see the note on `portalTo` below. */
	portalTo: HTMLElement | boolean
	/** How many labels to spell out before collapsing to "N selected". */
	maxInline?: number
	/** A search field at the top of the panel — on for the long lists, off for a fixed
	 *  three-value criteria list. */
	searchable?: boolean
	/** Search field placeholder. Read only when `searchable`. */
	searchPlaceholder?: string
	disabled?: boolean
	className?: string
}

const GAP = 4
/** Design cap on the panel. The room below the trigger can only lower it. */
const MAX_H = 288

// An AppButton centres its content, and `justify-between` cannot beat the `cg-` class.
const triggerFit = 'w-full [&>button]:w-full [&>button]:justify-between'

// `SearchableMenu` rows carry no focus style, so draw the DS ring, not Chrome's.
const searchRowFocus =
	'[&_[role=option]]:outline-none [&_[role=option]:focus-visible]:shadow-[0_0_0_2px_rgb(var(--cg-border-focus)_/_var(--cg-focus-ring-alpha,0.75))]'

/** Nearest scrolling ancestor — in the drawer that is the sheet body. */
const scrollBoxOf = (node: HTMLElement) => {
	let current = node.parentElement
	while (current) {
		const overflow = getComputedStyle(current).overflowY
		if (overflow === 'auto' || overflow === 'scroll') return current
		current = current.parentElement
	}
	return null
}

/**
 * Multi-select value picker: a `Dropdown`-shaped trigger over a list of checkbox rows
 * that stays open while you pick. The DS has no component for this — its two selects
 * are single-select and `SearchableMenu` always renders its search field.
 *
 * `portalTo` is not decoration: inside the rule drawer the panel MUST portal into the
 * drawer's own z-[9999] root, or it opens invisible under it.
 */
export const MultiSelect = ({
	options,
	values,
	onChange,
	placeholder,
	ariaLabel,
	portalTo,
	maxInline = 2,
	searchable,
	searchPlaceholder,
	disabled,
	className
}: MultiSelectProps) => {
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const [rect, setRect] = useState<DOMRect | null>(null)
	const [clipped, setClipped] = useState(false)

	// The panel is `position: fixed` off the trigger's rect, so it is re-measured when
	// anything moves it. The drawer body is the scroll container, hence capture phase.
	useLayoutEffect(() => {
		if (!open) return
		const measure = () => {
			const node = triggerRef.current
			if (!node) return
			const next = node.getBoundingClientRect()
			// Once the trigger scrolls out of its box the panel has nothing to point at.
			const clip = scrollBoxOf(node)?.getBoundingClientRect()
			setClipped(
				Boolean(clip && (next.bottom < clip.top || next.top > clip.bottom))
			)
			// Straight to the node as well as through state: state alone lands two frames late.
			const panel = panelRef.current
			if (panel) {
				panel.style.top = `${next.bottom + GAP}px`
				panel.style.left = `${next.left}px`
			}
			setRect(next)
		}
		measure()
		window.addEventListener('scroll', measure, true)
		window.addEventListener('resize', measure)
		return () => {
			window.removeEventListener('scroll', measure, true)
			window.removeEventListener('resize', measure)
		}
	}, [open])

	useEffect(() => {
		if (!open) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false)
		}
		const onPointerDown = (event: MouseEvent) => {
			const target = event.target as Node
			if (triggerRef.current?.contains(target)) return
			if (panelRef.current?.contains(target)) return
			setOpen(false)
		}
		document.addEventListener('keydown', onKeyDown)
		document.addEventListener('mousedown', onPointerDown)
		return () => {
			document.removeEventListener('keydown', onKeyDown)
			document.removeEventListener('mousedown', onPointerDown)
		}
	}, [open])

	// The DS `Dropdown` room-fit: clamp to the space below the trigger, three-row floor.
	const { fitHeight } = useMenuRoomFit({
		open,
		// The panel is not in the DOM until the trigger rect has been measured.
		ready: Boolean(rect),
		anchorRef: triggerRef,
		panelRef,
		gap: GAP,
		// The search header sits OUTSIDE the capped scroller, so the fit takes its height
		// off the cap or the panel overshoots.
		measureExtraChrome: searchable
			? (panel: HTMLElement) => {
					const list = panel.querySelector('[role="listbox"]')
					const header = list?.previousElementSibling as HTMLElement | null
					return header?.offsetHeight ?? 0
			  }
			: undefined
	})
	// The design cap still wins when it is the smaller of the two.
	const maxHeight = fitHeight === null ? MAX_H : Math.min(MAX_H, fitHeight)

	const toggle = (value: string) =>
		onChange(
			values.includes(value)
				? values.filter((entry) => entry !== value)
				: [...values, value]
		)

	// Emptiness and the count come from `values`, not from how many `options` can name:
	// a selected id with no option behind it used to show the PLACEHOLDER, and `toggle`
	// appends, so "setting" a field that read as blank would scope the rule to two.
	const picked = options.filter((option) => values.includes(option.value))
	const summary = !values.length
		? placeholder
		: picked.length === values.length && values.length <= maxInline
		? picked.map((option) => option.label).join(', ')
		: `${values.length} selected`

	const panel =
		open && rect && !clipped ? (
			<div
				ref={panelRef}
				style={{
					position: 'fixed',
					left: rect.left,
					top: rect.bottom + GAP,
					minWidth: rect.width,
					zIndex: 1
				}}
			>
				{searchable ? (
					<SearchableMenu
						multiple
						options={options.map((option) => ({
							value: option.value,
							label: option.label,
							leadingIcon: option.platform ? (
								<IntegrationLogo
									provider={option.platform}
									size={16}
									decorative
								/>
							) : undefined
						}))}
						selectedValues={values}
						onSelect={(option) => toggle(option.value)}
						maxHeight={maxHeight}
						searchPlaceholder={searchPlaceholder ?? 'Search'}
						searchAriaLabel={`Search ${ariaLabel.toLowerCase()}`}
						className={searchRowFocus}
					/>
				) : (
					<MenuList
						role="listbox"
						aria-label={ariaLabel}
						maxHeight={maxHeight}
						// Room for the focus ring: MenuList's `overflow-y: auto` scroller
						// forces `overflow-x` with it and clipped the DS ring at the panel
						// edge. The -2px margin takes that room back so the rows do not move.
						className="[&>div]:m-[-2px] [&>div]:p-[2px]"
					>
						{options.map((option) => {
							const checked = values.includes(option.value)
							return (
								<ListItem
									key={option.value}
									as="div"
									role="option"
									aria-selected={checked}
									tabIndex={0}
									// The mark goes INSIDE the label: the DS `leadingIcon` slot renders
									// before `leadingControl` and would push it ahead of the checkbox.
									label={
										option.platform ? (
											<span className="flex min-w-0 items-center gap-2">
												<IntegrationLogo
													provider={option.platform}
													size={16}
													decorative
													className="shrink-0"
												/>
												<span className="min-w-0 truncate">{option.label}</span>
											</span>
										) : (
											option.label
										)
									}
									// The real control is the row: the checkbox is a read-only mirror.
									leadingControl={
										<Checkbox
											size="sm"
											checked={checked}
											readOnly
											tabIndex={-1}
											aria-hidden
											className="pointer-events-none"
										/>
									}
									onClick={() => toggle(option.value)}
									onKeyDown={(event: ReactKeyboardEvent<HTMLDivElement>) => {
										if (event.key !== 'Enter' && event.key !== ' ') return
										event.preventDefault()
										toggle(option.value)
									}}
								/>
							)
						})}
					</MenuList>
				)}
			</div>
		) : null

	const portalRoot =
		portalTo === true || portalTo === false ? document.body : portalTo

	return (
		<div ref={triggerRef} className={`${triggerFit} ${className ?? ''}`}>
			<AppButton
				type="button"
				variant="secondary"
				size="md"
				roundness="default"
				label={
					<span
						className={`min-w-0 truncate ${
							values.length ? '' : 'text-[rgb(var(--cg-text-tertiary))]'
						}`}
					>
						{summary}
					</span>
				}
				rightIcon={<ChevronDown />}
				selected={open}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={ariaLabel}
				disabled={disabled}
				onClick={() => setOpen((current) => !current)}
			/>
			{panel && portalRoot ? createPortal(panel, portalRoot) : null}
		</div>
	)
}
